<script setup lang="ts">
/**
 * A notice that the password needs changing: it has appeared in a public breach, or an administrator has required a
 * new one. Not dismissible - the way to clear it is to change the password. Drawn inline by the pages that host it
 * (Home, Sign-in & Security), not floated over every screen. Draws nothing otherwise.
 *
 * @changelog
 *
 * 2026-10-10 (0.1.0) (jhoward)
 *   - Add PasswordSecurityBanner.vue.
 *
 * 2026-10-21 (0.2.0) (jhoward)
 *   - Draw inline in the page instead of fixed over every route, in a quieter style, and show it on the security page.
 */

import { computed } from 'vue';
import type { User } from '@/defs/user';
import useAppStore from '@/store';

const appStore = useAppStore();

const user = computed<User | null>(() => appStore.state.user as User | null);

const kind = computed<'reset' | 'breached' | null>(() => {
  if (!user.value || appStore.state.twoFactorAuthRequired) {
    return null;
  }
  if (user.value.password_reset_required) {
    return 'reset';
  }
  if (user.value.password_breached) {
    return 'breached';
  }
  return null;
});
</script>

<template>
  <section v-if="kind" class="password_banner--container" role="status">
    <span class="material-icons password_banner--icon" aria-hidden="true">gpp_maybe</span>
    <div>
      <p class="password_banner--headline">
        {{ 'reset' === kind ? 'A password change is required on your account.' : 'Your password appeared in a known data breach.' }}
      </p>
      <p class="password_banner--detail">
        {{
          'reset' === kind
            ? 'Please choose a new password to continue booking offers.'
            : 'Change it now to keep your account secure.'
        }}
      </p>
    </div>
    <router-link class="password_banner--action" :to="{ name: 'account.security', hash: '#password' }">
      Change password
    </router-link>
  </section>
</template>

<style scoped>
.password_banner--container {
  display: grid;
  grid-template-columns: max-content 1fr max-content;
  align-items: center;
  grid-gap: 0.75em;
  padding: 0.75em 1em;
  margin: 0 0 1em 0;
  background-color: rgba(var(--status-danger, 198, 40, 40), 0.08);
  border: 1px solid rgba(var(--status-danger, 198, 40, 40), 0.35);
  border-left-width: 4px;
  border-radius: 0.25em;
}

.password_banner--icon {
  font-size: 1.5rem;
  color: rgb(var(--status-danger, 198, 40, 40));
}

.password_banner--headline {
  margin: 0;
  font-weight: 600;
}

.password_banner--detail {
  margin: 0.25em 0 0 0;
  font-size: 0.9rem;
}

.password_banner--action {
  padding: 0.5em 0.75em;
  color: rgb(var(--status-danger, 198, 40, 40));
  text-decoration: none;
  text-transform: uppercase;
  border: 1px solid currentColor;
  border-radius: 0.25em;
  white-space: nowrap;
}

.password_banner--action:hover,
.password_banner--action:focus {
  background: rgba(var(--status-danger, 198, 40, 40), 0.1);
}

@media (max-width: 500px) {
  .password_banner--container {
    grid-template-columns: max-content 1fr;
  }

  .password_banner--action {
    grid-column: 2;
    justify-self: start;
  }
}
</style>
